"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useStrategiesStore, selectStrategyById } from "@/stores/strategiesStore";
import { usePaperTradingStore } from "@/stores/paperTradingStore";
import { useAllocations } from "@/hooks/useAllocations";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useAuth } from "@/hooks/useAuth";
import { useCftcGate } from "@/hooks/useCftcGate";
import { useTenant } from "@/hooks/useTenant";
import { FirstTradeDisclosureModal } from "@/components/trade/FirstTradeDisclosureModal";
import { isTenantGateReady } from "@/components/tenant/TenantContextProvider";
import { MarketViewBadge } from "./StrategyCard/MarketViewBadge";
import { PriceBadge } from "./StrategyCard/PriceBadge";
import { OfferedBy } from "./OfferedBy";
import { SHOW_STRATEGY_PERFORMANCE } from "@/utils/constants";

interface AllocationModalProps {
  isOpen: boolean;
  strategyId: string | null;
  onClose: () => void;
}

const MIN_ALLOCATION = 100;

export const AllocationModal = ({
  isOpen,
  strategyId,
  onClose,
}: AllocationModalProps) => {
  const [amount, setAmount] = useState("");
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const prevStrategyIdRef = useRef<string | null>(null);

  const store = useStrategiesStore();
  const strategy = strategyId ? selectStrategyById(store, strategyId) : undefined;
  const { account } = usePaperTradingStore();
  const { portfolio } = usePortfolio();
  const { allocate, isAllocating } = useAllocations();
  const { isAuthenticated } = useAuth();
  // SFWL-017: CFTC §1.55(b) acknowledgement gate, shared with
  // ApplyConfirmationModal and PaperTradingPanel via the same
  // tenant+user localStorage key.
  const { runGated, reset: resetGate, modalProps } = useCftcGate();
  // SFWL-017 Codex P1 fix: while tenant resolution is "pending" or "failed"
  // we don't yet know whether this tenant is futures-scoped, so submission
  // must fail closed rather than trust DEFAULT_TENANT_CONTEXT's open
  // "all" scope.
  const { resolution } = useTenant();
  const tenantGateReady = isTenantGateReady(resolution);

  // Use portfolio available balance, fallback to paper trading account, then default demo balance
  const availableBalance =
    portfolio?.availableBalance ?? account?.availableBalance ?? 500000;

  // Handle mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset form when strategy changes
  useEffect(() => {
    if (strategyId && strategyId !== prevStrategyIdRef.current) {
      setAmount("");
      setRiskAcknowledged(false);
      setError(null);
    }
    prevStrategyIdRef.current = strategyId;
  }, [strategyId]);

  // SFWL-017 (P3 review fix): pendingAction lives in useCftcGate's own
  // state, keyed to this component instance, not to `isOpen` — this modal
  // stays mounted (hooks run every render) even while it renders `null`, so
  // a stash left behind by a parent-driven close would otherwise survive
  // and auto-open the disclosure the next time this modal opens, before the
  // user has submitted anything this time around.
  useEffect(() => {
    if (!isOpen) {
      resetGate();
    }
  }, [isOpen, resetGate]);

  // Handle escape key — SFWL-017: while the CFTC disclosure is open, Escape
  // must close only the disclosure (FirstTradeDisclosureModal's own Escape
  // handler does that via modalProps.onClose), not this allocation modal —
  // otherwise the in-progress form (amount, checkbox) would be discarded
  // along with the disclosure.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !modalProps.open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose, modalProps.open]);

  if (!mounted || !isOpen || !strategy) return null;

  // SFWL-017 (P3 review fix): the disclosure has no focus trap, so the form
  // behind it is still keyboard-reachable (Shift+Tab) while open — without
  // this, a user could edit the amount or uncheck the sim box after the
  // gated action is stashed, then acknowledge, and the frozen closure would
  // submit stash-time values that contradict what's now on screen. Freezing
  // every interactive control while the disclosure is up keeps
  // stash-time === acknowledge-time.
  const disclosureOpen = modalProps.open;
  const amountNum = parseFloat(amount) || 0;
  const isValidAmount = amountNum >= MIN_ALLOCATION && amountNum <= availableBalance;
  const canSubmit =
    isValidAmount &&
    riskAcknowledged &&
    !isSubmitting &&
    !isAllocating &&
    !disclosureOpen &&
    tenantGateReady;

  // Estimated annual return based on 1-year performance
  const estimatedReturn = amountNum * (strategy.yearReturn / 100);

  const handleQuickSelect = (percent: number) => {
    const value = (availableBalance * percent) / 100;
    setAmount(value.toFixed(2));
  };

  const performAllocation = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Call the real API to allocate. riskAcknowledged is the live
      // checkbox value at submit time — SFWL-017 (previously hardcoded to
      // `true` inside useAllocations.ts, regardless of what the user
      // actually checked).
      await allocate({
        strategyId: strategy.id,
        amount: amountNum,
        venue: "demo",
        riskAcknowledged,
      });

      // Close modal on success - React Query will invalidate and refetch data
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to allocate");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // SFWL-017: futures-scoped tenants must acknowledge the CFTC §1.55(b)
    // disclosure before the allocation actually fires; non-futures tenants
    // and already-acknowledged tenant+user pairs pass straight through.
    runGated(performAllocation);
  };

  // SFWL-017: backdrop click has no `disabled` attribute to lean on (it's a
  // div, not a button) — guard the handler itself so it can't close this
  // modal (and drop the pending allocation) while the disclosure is up.
  const handleCancel = () => {
    if (disclosureOpen) return;
    resetGate();
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <FirstTradeDisclosureModal {...modalProps} />
      <div className="fixed inset-0 bg-black/50" onClick={handleCancel} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-slate-900 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-white">Allocate to Strategy</h2>

        {/* Strategy Info */}
        <div className="mb-4 rounded-lg bg-slate-800 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MarketViewBadge view={strategy.marketView} />
              <span className="font-semibold text-white">{strategy.name}</span>
            </div>
            <PriceBadge resolvedPrice={strategy.resolvedPrice} />
          </div>
          {/* Issue #131: return/AUM figures are disputed pending an exporter
              fix + restatement — hidden until SHOW_STRATEGY_PERFORMANCE
              flips back to true. */}
          <div
            className={clsx(
              "mt-3 grid gap-3 text-xs",
              SHOW_STRATEGY_PERFORMANCE ? "grid-cols-3" : "grid-cols-1"
            )}
          >
            {SHOW_STRATEGY_PERFORMANCE && (
              <>
                <div>
                  <p className="text-slate-500">1Y Return</p>
                  <p
                    className={clsx(
                      "font-semibold",
                      strategy.yearReturn >= 0 ? "text-emerald-400" : "text-rose-400"
                    )}
                  >
                    {strategy.yearReturn >= 0 ? "+" : ""}
                    {strategy.yearReturn.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">AUM</p>
                  <p className="font-semibold text-white">
                    ${(strategy.totalAum / 1000000).toFixed(1)}M
                  </p>
                </div>
              </>
            )}
            <div>
              <p className="text-slate-500">Risk</p>
              <p
                className={clsx(
                  "font-semibold capitalize",
                  strategy.riskLevel === "low"
                    ? "text-emerald-400"
                    : strategy.riskLevel === "medium"
                      ? "text-amber-400"
                      : "text-rose-400"
                )}
              >
                {strategy.riskLevel}
              </p>
            </div>
          </div>
        </div>

        {/* Third-party attribution */}
        {strategy.ownerType === "third_party" && (
          <OfferedBy
            ownerName={strategy.ownerName}
            ownerLogoUrl={strategy.ownerLogoUrl}
            disclosureUrl={strategy.disclosureUrl}
            className="mb-4"
          />
        )}

        {/* Auth Prompt */}
        {!isAuthenticated && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-center">
            <p className="text-sm text-amber-300">
              Sign in to allocate capital to this strategy.
            </p>
          </div>
        )}

        {/* Available Balance */}
        {isAuthenticated && (
          <div className="mb-4 flex justify-between text-sm">
            <span className="text-slate-400">Available Balance</span>
            <span className="font-semibold text-white">
              ${availableBalance.toLocaleString()}
            </span>
          </div>
        )}

        {/* Allocation Form — only when authenticated */}
        {isAuthenticated && (
          <>
            {/* Amount Input */}
            <div className="mb-4">
              <label className="mb-1 block text-xs text-slate-400">
                Amount to Allocate (min ${MIN_ALLOCATION})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  $
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-slate-600 bg-slate-800 py-2.5 pl-7 pr-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
                  min={MIN_ALLOCATION}
                  max={availableBalance}
                  step="0.01"
                  disabled={disclosureOpen}
                />
              </div>

              {/* Quick Select */}
              <div className="mt-2 flex gap-2">
                {[25, 50, 75, 100].map((pct) => (
                  <button
                    key={pct}
                    onClick={() => handleQuickSelect(pct)}
                    disabled={disclosureOpen}
                    className="flex-1 rounded border border-slate-600 py-1.5 text-xs text-slate-400 transition hover:border-slate-500 hover:text-white disabled:opacity-50"
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>

            {/* Estimated Return — Issue #131: derived from yearReturn, which
                is disputed pending an exporter fix + restatement; hidden
                until SHOW_STRATEGY_PERFORMANCE flips back to true. */}
            {SHOW_STRATEGY_PERFORMANCE && amountNum > 0 && (
              <div className="mb-4 rounded-lg bg-slate-800/50 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Est. Annual Return</span>
                  <span
                    className={clsx(
                      "font-semibold",
                      estimatedReturn >= 0 ? "text-emerald-400" : "text-rose-400"
                    )}
                  >
                    {estimatedReturn >= 0 ? "+" : ""}${estimatedReturn.toFixed(2)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Based on historical 1-year performance. Past returns do not guarantee
                  future results.
                </p>
              </div>
            )}

            {/* Risk Acknowledgment */}
            <label className="mb-4 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={riskAcknowledged}
                onChange={(e) => setRiskAcknowledged(e.target.checked)}
                disabled={disclosureOpen}
                className="mt-1 rounded border-slate-600 bg-slate-800 disabled:opacity-50"
              />
              <span className="text-slate-400">
                I understand this is a paper trading simulation and that real trading
                involves significant risk of loss.
              </span>
            </label>

            {/* Error */}
            {error && (
              <div className="mb-4 rounded-lg bg-rose-500/20 px-3 py-2 text-sm text-rose-400">
                {error}
              </div>
            )}

            {/* Validation Message */}
            {amount && !isValidAmount && (
              <p className="mb-4 text-xs text-rose-400">
                {amountNum < MIN_ALLOCATION
                  ? `Minimum allocation is $${MIN_ALLOCATION}`
                  : `Amount exceeds available balance`}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                disabled={isSubmitting || disclosureOpen}
                className="flex-1 rounded-lg border border-slate-600 py-2.5 text-sm text-slate-300 transition hover:bg-slate-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex-1 rounded-lg bg-blue-500 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-500/50"
              >
                {isSubmitting || isAllocating ? "Allocating..." : "Confirm Allocation"}
              </button>
            </div>
          </>
        )}

        {/* Close button when not authenticated */}
        {!isAuthenticated && (
          <button
            onClick={onClose}
            className="w-full rounded-lg border border-slate-600 py-2.5 text-sm text-slate-300 transition hover:bg-slate-800"
          >
            Close
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};
